/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import ApiKey from '../../../models/apiKeyModel';
import {onSetCrumbs} from '../../../util/events';
import {ResponseData} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../../util/utils';
import {trackPlatformAction} from '../../../util/analytics';
import ApiSettings from './apiSettings';
import {ApiKeyStatus} from '../../../util/interfaces';
import {onConnectBolOauth} from '../../../util/bolOauthUtil';
import {saveToLocal} from '../../../util/storageUtil';
import {SELECTED_ACCOUNT_KEY} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function ApiSettingsController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [apiKeys, setApiKeys] = useState<Array<ApiKey>>([]);
    const [loading, setLoading] = useState(true);

    function onRenew(evt: React.MouseEvent) {
        const {account} = context.accountContext;
        if (!account) return;
        const {user} = context.userContext;
        if (user?.isDemoAccount)
            return showDialog(
                t('settings.api.demoNotAllowedMsg', 'Deze actie is niet toegestaan in het demo account.'),
                t('settings.api.demoNotAllowedTitle', 'Actie niet toegestaan')
            );
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth(evt.shiftKey);
    }

    const onClickActivate = (apiKey: ApiKey) => (evt: React.MouseEvent) => {
        const {account} = context.accountContext;
        if (!account) return;
        if (apiKey.useOAuth) {
            saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
            return onConnectBolOauth(evt.shiftKey);
        }
        setLoading(true);
        apiKey.activate((apiKey: ApiKey, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                trackPlatformAction('settings.api', 'save_settings');
                if (apiKey.status === ApiKeyStatus.ERROR) {
                    showDialog(
                        t(
                            'settings.api.expiredMsg',
                            'De API koppeling is verlopen en kan niet meer gebruikt worden. Je kunt in je Bol account nieuwe API sleutels aanmaken en de API koppeling daarmee instellen via de Wijzigen optie van de API koppeling.'
                        ),
                        t('settings.api.expiredTitle', 'Koppeling verlopen')
                    );
                } else showSnackbar(t('settings.api.activated', 'De API koppeling is geactiveerd'));
                loadKeys();
            } else
                showDialog(
                    data.message ??
                        createErrorMessage(
                            t('settings.api.activateFailedMsg', 'Het activeren van de API key is niet gelukt.')
                        ),
                    t('settings.api.activateFailedTitle', 'Activeren mislukt')
                );
        });
    };

    function onDeactivateKey(apiKey: ApiKey) {
        return function () {
            showDialog(
                t('settings.api.disconnectConfirm', 'Weet je zeker dat je deze API koppeling wilt verbreken?'),
                t('settings.api.disconnectTitle', 'API koppeling verbreken'),
                [
                    t('settings.api.no', 'Nee'),
                    {label: t('settings.api.yes', 'Ja'), onClick: onConfirmDeactivate(apiKey), primary: false}
                ]
            );
        };
    }

    function onConfirmDeactivate(apiKey: ApiKey) {
        return function () {
            const {account} = context.accountContext;
            if (!account) return;
            setLoading(true);
            apiKey.deactivate((apiKey: ApiKey, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar(t('settings.api.disconnected', 'De API koppeling is verbroken'));
                    account.replaceApiKey(apiKey);
                    context.onChangeContext({account});
                    loadKeys();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'settings.api.deactivateFailedMsg',
                                    'Het deactiveren van de API koppeling is niet gelukt.'
                                )
                        ),
                        t('settings.api.deactivateFailedTitle', 'Deactivatie mislukt')
                    );
            });
        };
    }

    function loadKeys() {
        const {account} = context.accountContext;
        if (account) {
            if (!loading) setLoading(true);
            ApiKey.getAll(account.id, (apiKeys: ApiKey[], success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setApiKeys(apiKeys);
                } else
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t('settings.api.loadFailedMsg', 'Het laden van je API keys is niet gelukt.')
                            ),
                        t('settings.api.loadFailedTitle', 'Laden mislukt')
                    );
            });
        }
    }

    useEffect(() => {
        loadKeys();
    }, [context.accountContext.account]);

    useEffect(() => {
        onSetCrumbs([
            {label: t('settings.api.crumbSettings', 'Instellingen')},
            {label: t('settings.api.crumbApi', 'API')}
        ]);
    }, []);

    return (
        <ApiSettings
            onClickActivate={onClickActivate}
            apiKeys={apiKeys}
            loading={loading}
            onClickAdd={onRenew}
            onRenew={onRenew}
            onDeactivateKey={onDeactivateKey}
        />
    );
}
